import { isEmpty, map, sortedIndex, values } from "lodash";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { matchPath, useLocation } from "react-router-dom";
import { isActiveInstall } from "../../pages/Workspace/InstallsData/utils";
import useMapConfig from "../../utils/useMapConfig";
import { useHotspots } from "../hotspots";
import { useInstalls } from "../installs";
import { useLocations } from "../locations";

type ContextValue = {
  tooCloseToWitnessData: TooCloseToWitnessResponse | undefined;
};

const TooCloseToWitnessContext = React.createContext<ContextValue | undefined>(
  undefined
);

interface Props {}

export const TooCloseToWitnessProvider: React.FC<Props> = (props) => {
  const { pathname } = useLocation();
  const { hotspotsAroundCenter, getHotspotCoordinates, hotspotsFetchStatus } =
    useHotspots();
  const { installs } = useInstalls();
  const {
    locationsAroundCenter,
    locationsFetchStatus,
    getLocationCoordinates
  } = useLocations();
  const fetchingFailed = useRef(false);
  const lastFetchedH4Indices = useRef<string[]>([]);
  const [tooCloseToWitnessData, setNeighborsTooCloseToWitnessData] = useState<
    TooCloseToWitnessResponse | undefined
  >();
  const visibleLayers = useMapConfig((state) => state.visibleLayers);

  const activeInstallLocationUuids = useMemo(
    () => map(values(installs).filter(isActiveInstall), "location_uuid"),
    [installs]
  );

  const fetchNeighborsTooCloseToWitness = useCallback(
    async (h3Indexes: string[]) => {
      lastFetchedH4Indices.current = h3Indexes;

      try {
        const res = await fetch(
          `${process.env.REACT_APP_ETL_API}/hotspots/too-close-to-witness-lean/`,
          {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({
              h3Indexes
            })
          }
        );

        const response: TooCloseToWitnessResponse = await res.json();

        setNeighborsTooCloseToWitnessData(response);
      } catch (e) {
        fetchingFailed.current = true;
        console.error(e);
      }
    },
    [setNeighborsTooCloseToWitnessData]
  );

  const detailsPageMatch = matchPath<{
    tab: string; // hotspots, locations, hexes, contacts, installs
    id: string;
    section: string;
    subsection?: string;
  }>(pathname, {
    path: ["/:tab/:id/:section/:subsection", "/:tab/:id/:section"]
  });

  const tooCloseToWitnessDataIsNeeded = useMemo(
    () =>
      visibleLayers.tooCloseToWitness ||
      detailsPageMatch?.params.section === "witnesses",
    [detailsPageMatch?.params.section, visibleLayers.tooCloseToWitness]
  );

  useEffect(() => {
    if (locationsFetchStatus.current !== "ready") return;
    if (hotspotsFetchStatus.current !== "ready") return;

    const h3Indexes: string[] = [];

    locationsAroundCenter
      .filter((location) => !activeInstallLocationUuids.includes(location.uuid))
      .forEach((location) => {
        const coordinates = getLocationCoordinates(location.uuid);
        if (coordinates?.h3Index) {
          const idx = sortedIndex(h3Indexes, coordinates.h3Index);
          h3Indexes.splice(idx, 0, coordinates.h3Index);
        }
      });

    hotspotsAroundCenter.forEach((hotspot) => {
      const coordinates = getHotspotCoordinates(hotspot.id);
      if (coordinates?.h3Index) {
        const idx = sortedIndex(h3Indexes, coordinates.h3Index);
        h3Indexes.splice(idx, 0, coordinates.h3Index);
      }
    });

    if (h3Indexes.length === lastFetchedH4Indices.current.length) {
      let areArraysDifferent = false;
      for (let index = 0; index < h3Indexes.length; index++) {
        if (h3Indexes[index] !== lastFetchedH4Indices.current[index]) {
          areArraysDifferent = true;
          break;
        }
      }

      if (!areArraysDifferent) return;
    }

    if (!isEmpty(h3Indexes) && tooCloseToWitnessDataIsNeeded) {
      fetchNeighborsTooCloseToWitness(h3Indexes);
    }
  }, [
    locationsFetchStatus,
    hotspotsFetchStatus,
    fetchNeighborsTooCloseToWitness,
    getHotspotCoordinates,
    hotspotsAroundCenter,
    locationsAroundCenter,
    activeInstallLocationUuids,
    getLocationCoordinates,
    visibleLayers,
    tooCloseToWitnessDataIsNeeded
  ]);

  const value = useMemo(
    () => ({
      tooCloseToWitnessData
    }),
    [tooCloseToWitnessData]
  );

  return <TooCloseToWitnessContext.Provider value={value} {...props} />;
};

export const useTooCloseToWitness = (): ContextValue => {
  const context = useContext(TooCloseToWitnessContext);
  if (context === undefined) {
    throw new Error(
      "useTooCloseToWitness must be used within an TooCloseToWitnessProvider"
    );
  }
  return context;
};

//
// Utils
//

export interface TooCloseToWitnessData {
  startIndex: string;
  info: {
    endIndex: string;
    distanceR11: number;
    distanceMeters: number;
  };
}

export interface TooCloseToWitnessLineCoordinates {
  laA: number;
  loA: number;
  IA: string;
  laB: number;
  loB: number;
  IB: string;
}

interface TooCloseToWitnessResponse {
  object: "list";
  url: "/hotspots/too-close-to-witness-lean/";
  coordinates: TooCloseToWitnessLineCoordinates[];
  h3Indexes: TooCloseToWitnessData[];
}
